import React, { useState } from 'react';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, LoaderIcon, AlertCircleIcon, TruckIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
export function LoginPage() {
  const {
    login
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const result = await login(email.trim(), password);
      if (!result.success) {
        setLoginError(result.error || 'Credenciales incorrectas');
      }
    } catch {
      setLoginError('Error de conexión. Intenta de nuevo.');
    } finally {
      setIsLoggingIn(false);
    }
  };
  return <div className="min-h-screen w-full flex font-sans bg-white" data-id="element-2373">
      {/* --- CSS Animations --- */}
      <style data-id="element-2374">
        {`
          @keyframes fadeUp {
            0% { opacity: 0; transform: translateY(24px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeScale {
            0% { opacity: 0; transform: scale(0.85); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes float1 {
            0%, 100% { transform: translateY(0) translateX(0); }
            50% { transform: translateY(-20px) translateX(12px); }
          }
          @keyframes float2 {
            0%, 100% { transform: translateY(0) translateX(0); }
            50% { transform: translateY(15px) translateX(-15px); }
          }
          @keyframes float3 {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50% { transform: translateY(-25px) rotate(10deg); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-200%) skewX(-15deg); }
            100% { transform: translateX(300%) skewX(-15deg); }
          }
          @keyframes breathe {
            0%, 100% { transform: scale(1) translate(-50%, -50%); opacity: 0.2; }
            50% { transform: scale(1.1) translate(-45%, -45%); opacity: 0.4; }
          }
          @keyframes driveTruck {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(calc(100vw)); }
          }
          @keyframes spinWheel {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes floatBox {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(-12px) rotate(3deg); }
            75% { transform: translateY(8px) rotate(-2deg); }
          }
          @keyframes dashRoad {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -40; }
          }
          @keyframes truckBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }

          .anim-fade-up { animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
          .anim-fade-scale { animation: fadeScale 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
          .anim-fade-in { animation: fadeIn 0.8s ease-out forwards; opacity: 0; }
          .anim-float-1 { animation: float1 15s ease-in-out infinite; }
          .anim-float-2 { animation: float2 18s ease-in-out infinite; }
          .anim-float-3 { animation: float3 22s ease-in-out infinite; }
          .anim-breathe { animation: breathe 10s ease-in-out infinite; }
        `}
      </style>

      {/* ========================================== */}
      {/* LEFT PANEL (Hidden on Mobile, Visible lg+) */}
      {/* ========================================== */}
      <div className="hidden lg:flex flex-col w-[45%] relative overflow-hidden bg-gradient-to-br from-[#060c1e] to-[#0D1B46]" data-id="element-2375">
        {/* Animated Background Glow */}
        <div className="absolute top-1/2 left-1/2 pointer-events-none anim-breathe" style={{
        width: '120%',
        height: '120%',
        background: 'radial-gradient(circle, rgba(41,82,196,0.15) 0%, transparent 60%)',
        transformOrigin: 'center center'
      }} data-id="element-2376" />

        {/* --- Animated Delivery/Logistics Elements --- */}

        {/* Road with dashed line at bottom */}
        <div className="absolute bottom-[18%] left-0 w-full" style={{
        height: '40px'
      }} data-id="element-2377">
          <svg viewBox="0 0 800 40" fill="none" className="w-full h-full" preserveAspectRatio="none" data-id="element-2378">
            {/* Road surface */}
            <rect x="0" y="15" width="800" height="18" rx="2" fill="rgba(41,82,196,0.08)" data-id="element-2379" />
            {/* Dashed center line */}
            <line x1="0" y1="24" x2="800" y2="24" stroke="#3B65D7" strokeWidth="2" strokeDasharray="20 20" opacity="0.25" style={{
            animation: 'dashRoad 2s linear infinite'
          }} data-id="element-2380" />
          </svg>
        </div>

        {/* Animated Truck driving across */}
        <div className="absolute bottom-[19%] pointer-events-none" style={{
        width: '140px',
        height: '70px',
        animation: 'driveTruck 12s linear infinite'
      }} data-id="element-2381">
          <div style={{
          animation: 'truckBounce 0.8s ease-in-out infinite'
        }} data-id="element-2382">
            <svg viewBox="0 0 180 90" fill="none" className="w-full h-full" data-id="element-2383">
              {/* Truck body (cargo area) */}
              <rect x="10" y="20" width="90" height="45" rx="4" fill="#2952C4" opacity="0.6" data-id="element-2384" />
              <rect x="12" y="22" width="86" height="41" rx="3" fill="#3B65D7" opacity="0.4" data-id="element-2385" />
              {/* Medical cross on truck */}
              <rect x="47" y="32" width="16" height="4" rx="1" fill="#eef2fc" opacity="0.6" data-id="element-2386" />
              <rect x="53" y="26" width="4" height="16" rx="1" fill="#eef2fc" opacity="0.6" data-id="element-2387" />
              {/* Cabin */}
              <path d="M100 30 L100 65 L140 65 L140 42 L125 30 Z" fill="#2952C4" opacity="0.7" data-id="element-2388" />
              {/* Window */}
              <path d="M105 35 L120 35 L130 45 L105 45 Z" fill="#5483DF" opacity="0.5" data-id="element-2389" />
              {/* Bumper */}
              <rect x="138" y="50" width="8" height="15" rx="2" fill="#2952C4" opacity="0.5" data-id="element-2390" />
              {/* Headlight */}
              <rect x="143" y="52" width="5" height="6" rx="1.5" fill="#eef2fc" opacity="0.7" data-id="element-2391" />
              {/* Wheels */}
              <g style={{
              transformOrigin: '35px 70px',
              animation: 'spinWheel 1s linear infinite'
            }} data-id="element-2392">
                <circle cx="35" cy="70" r="10" fill="#0D1B46" opacity="0.8" data-id="element-2393" />
                <circle cx="35" cy="70" r="6" fill="#1a2d5e" opacity="0.9" data-id="element-2394" />
                <circle cx="35" cy="70" r="2" fill="#3B65D7" opacity="0.6" data-id="element-2395" />
              </g>
              <g style={{
              transformOrigin: '120px 70px',
              animation: 'spinWheel 1s linear infinite'
            }} data-id="element-2396">
                <circle cx="120" cy="70" r="10" fill="#0D1B46" opacity="0.8" data-id="element-2397" />
                <circle cx="120" cy="70" r="6" fill="#1a2d5e" opacity="0.9" data-id="element-2398" />
                <circle cx="120" cy="70" r="2" fill="#3B65D7" opacity="0.6" data-id="element-2399" />
              </g>
              {/* Exhaust smoke */}
              <circle cx="5" cy="60" r="4" fill="#5483DF" opacity="0.15" data-id="element-2400" />
              <circle cx="-5" cy="55" r="6" fill="#5483DF" opacity="0.1" data-id="element-2401" />
            </svg>
          </div>
        </div>

        {/* Second truck (smaller, further back, slower) */}
        <div className="absolute bottom-[26%] pointer-events-none opacity-30" style={{
        width: '80px',
        height: '40px',
        animation: 'driveTruck 20s linear infinite',
        animationDelay: '-8s'
      }} data-id="element-2402">
          <svg viewBox="0 0 180 90" fill="none" className="w-full h-full" data-id="element-2403">
            <rect x="10" y="20" width="90" height="45" rx="4" fill="#3B65D7" opacity="0.5" data-id="element-2404" />
            <path d="M100 30 L100 65 L140 65 L140 42 L125 30 Z" fill="#3B65D7" opacity="0.5" data-id="element-2405" />
            <circle cx="35" cy="70" r="10" fill="#0D1B46" opacity="0.6" data-id="element-2406" />
            <circle cx="120" cy="70" r="10" fill="#0D1B46" opacity="0.6" data-id="element-2407" />
          </svg>
        </div>

        {/* Floating Package Boxes */}
        <div className="absolute top-[20%] left-[18%] opacity-25" style={{
        width: '50px',
        height: '50px',
        animation: 'floatBox 8s ease-in-out infinite'
      }} data-id="element-2408">
          <svg viewBox="0 0 50 50" fill="none" data-id="element-2409">
            <rect x="5" y="12" width="40" height="32" rx="3" fill="#2952C4" opacity="0.6" stroke="#3B65D7" strokeWidth="1" data-id="element-2410" />
            <line x1="5" y1="22" x2="45" y2="22" stroke="#5483DF" strokeWidth="1" opacity="0.5" data-id="element-2411" />
            <line x1="25" y1="22" x2="25" y2="44" stroke="#5483DF" strokeWidth="1" opacity="0.3" data-id="element-2412" />
            <path d="M5 12 L25 5 L45 12" stroke="#3B65D7" strokeWidth="1" fill="#2952C4" opacity="0.4" data-id="element-2413" />
          </svg>
        </div>

        <div className="absolute top-[40%] right-[15%] opacity-20" style={{
        width: '35px',
        height: '35px',
        animation: 'floatBox 10s ease-in-out infinite',
        animationDelay: '-3s'
      }} data-id="element-2414">
          <svg viewBox="0 0 50 50" fill="none" data-id="element-2415">
            <rect x="5" y="12" width="40" height="32" rx="3" fill="#3B65D7" opacity="0.5" stroke="#5483DF" strokeWidth="1" data-id="element-2416" />
            <line x1="5" y1="22" x2="45" y2="22" stroke="#5483DF" strokeWidth="1" opacity="0.5" data-id="element-2417" />
            <path d="M5 12 L25 5 L45 12" stroke="#5483DF" strokeWidth="1" fill="#3B65D7" opacity="0.3" data-id="element-2418" />
          </svg>
        </div>

        <div className="absolute bottom-[45%] left-[40%] opacity-15" style={{
        width: '28px',
        height: '28px',
        animation: 'floatBox 12s ease-in-out infinite',
        animationDelay: '-6s'
      }} data-id="element-2419">
          <svg viewBox="0 0 50 50" fill="none" data-id="element-2420">
            <rect x="5" y="12" width="40" height="32" rx="3" fill="#5483DF" opacity="0.5" stroke="#5483DF" strokeWidth="1" data-id="element-2421" />
            <line x1="5" y1="22" x2="45" y2="22" stroke="#eef2fc" strokeWidth="1" opacity="0.3" data-id="element-2422" />
          </svg>
        </div>

        {/* Location pin markers along the "route" */}
        <div className="absolute top-[55%] left-[25%] opacity-20 anim-float-1" style={{
        width: '20px',
        height: '28px'
      }} data-id="element-2423">
          <svg viewBox="0 0 24 32" fill="#5483DF" data-id="element-2424">
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" opacity="0.6" data-id="element-2425" />
          </svg>
        </div>

        <div className="absolute top-[35%] right-[30%] opacity-15 anim-float-2" style={{
        width: '16px',
        height: '22px',
        animationDelay: '3s'
      }} data-id="element-2426">
          <svg viewBox="0 0 24 32" fill="#3B65D7" data-id="element-2427">
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z" opacity="0.6" data-id="element-2428" />
          </svg>
        </div>

        {/* Floating Orbs */}
        <div className="absolute top-[15%] left-[60%] w-3 h-3 rounded-full bg-[#5483DF] opacity-15 anim-float-3" data-id="element-2429" />
        <div className="absolute top-[65%] right-[35%] w-2 h-2 rounded-full bg-[#3B65D7] opacity-20 anim-float-1" style={{
        animationDelay: '2s'
      }} data-id="element-2430" />
        <div className="absolute bottom-[35%] left-[10%] w-4 h-4 rounded-full bg-[#2952C4] opacity-10 anim-float-2" style={{
        animationDelay: '4s'
      }} data-id="element-2431" />

        {/* --- Left Panel Content --- */}
        <div className="relative z-10 flex flex-col justify-center h-full px-16 lg:px-20 xl:px-24" data-id="element-2432">
          <div className="anim-fade-up" style={{
          animationDelay: '0.2s'
        }} data-id="element-2433">
            <div className="w-[88px] h-[88px] rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center mb-8 p-3" style={{
            boxShadow: '0 0 40px rgba(41,82,196,0.3)'
          }} data-id="element-2434">
              <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain drop-shadow-lg" data-id="element-2435" />
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight font-heading tracking-tight mb-4" data-id="element-2436">
              Miladys Rodríguez <br data-id="element-2437" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#5483DF] to-[#eef2fc]" data-id="element-2438">
                & Asoc.
              </span>
            </h1>
            <p className="text-lg text-[#5483DF] font-medium max-w-md leading-relaxed" data-id="element-2439">
              Sistema de Control de Vendedores
            </p>
          </div>
        </div>

        <div className="absolute bottom-8 left-16 lg:left-20 xl:left-24 anim-fade-in" style={{
        animationDelay: '0.8s'
      }} data-id="element-2440">
          <p className="text-sm text-slate-400/60 font-medium" data-id="element-2441">
            © 2026 Miladys Rodríguez & Asoc.
          </p>
        </div>
      </div>

      {/* ========================================== */}
      {/* RIGHT PANEL (Always Visible, Full on Mobile) */}
      {/* ========================================== */}
      <div className="flex-1 flex flex-col items-center justify-center relative bg-gradient-to-br from-[#060c1e] to-[#0D1B46] lg:bg-none lg:bg-white min-h-screen" data-id="element-2442">
        {/* Mobile dark background elements (hidden on lg+) */}
        <div className="lg:hidden absolute inset-0 pointer-events-none overflow-hidden" data-id="element-2443">
          {/* Breathing glow */}
          <div className="absolute top-1/2 left-1/2 anim-breathe" style={{
          width: '120%',
          height: '120%',
          background: 'radial-gradient(circle, rgba(41,82,196,0.12) 0%, transparent 60%)',
          transformOrigin: 'center center'
        }} data-id="element-2444" />
          {/* Floating orbs */}
          <div className="absolute top-[20%] left-[10%] w-3 h-3 rounded-full bg-[#5483DF] opacity-15 anim-float-1" data-id="element-2445" />
          <div className="absolute top-[60%] right-[15%] w-2 h-2 rounded-full bg-[#3B65D7] opacity-20 anim-float-2" style={{
          animationDelay: '2s'
        }} data-id="element-2446" />
          <div className="absolute bottom-[25%] left-[25%] w-4 h-4 rounded-full bg-[#2952C4] opacity-10 anim-float-3" style={{
          animationDelay: '4s'
        }} data-id="element-2447" />

          {/* Mobile: Small truck driving across bottom */}
          <div className="absolute bottom-[8%]" style={{
          width: '80px',
          height: '40px',
          animation: 'driveTruck 15s linear infinite',
          opacity: 0.2
        }} data-id="element-2448">
            <svg viewBox="0 0 180 90" fill="none" className="w-full h-full" data-id="element-2449">
              <rect x="10" y="20" width="90" height="45" rx="4" fill="#3B65D7" opacity="0.6" data-id="element-2450" />
              <path d="M100 30 L100 65 L140 65 L140 42 L125 30 Z" fill="#3B65D7" opacity="0.6" data-id="element-2451" />
              <circle cx="35" cy="70" r="10" fill="#0D1B46" opacity="0.8" data-id="element-2452" />
              <circle cx="120" cy="70" r="10" fill="#0D1B46" opacity="0.8" data-id="element-2453" />
            </svg>
          </div>

          {/* Mobile road line */}
          <div className="absolute bottom-[9%] left-0 w-full opacity-15" style={{
          height: '2px'
        }} data-id="element-2454">
            <svg viewBox="0 0 400 2" fill="none" className="w-full h-full" preserveAspectRatio="none" data-id="element-2455">
              <line x1="0" y1="1" x2="400" y2="1" stroke="#3B65D7" strokeWidth="2" strokeDasharray="12 12" style={{
              animation: 'dashRoad 2s linear infinite'
            }} data-id="element-2456" />
            </svg>
          </div>
        </div>

        {/* Subtle dot grid texture (desktop only) */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block" style={{
        backgroundImage: 'radial-gradient(circle, #0f1f4a 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        opacity: 0.02
      }} data-id="element-2457" />

        {/* Mobile: Logo + Branding */}
        <div className="lg:hidden flex flex-col items-center px-5 mb-6 anim-fade-in relative z-10" data-id="element-2458">
          <div className="w-[64px] h-[64px] rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 flex items-center justify-center mb-4 p-2" style={{
          boxShadow: '0 0 30px rgba(41,82,196,0.25)'
        }} data-id="element-2459">
            <img src="https://cdn.magicpatterns.com/uploads/x7GeSJnrf7zHwSFqA8Zq1u/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png" alt="MR Logo" className="w-full h-full object-contain" data-id="element-2460" />
          </div>
          <h2 className="text-xl font-bold text-white font-heading tracking-tight" data-id="element-2461">
            Miladys Rodríguez & Asoc.
          </h2>
          <p className="text-sm text-[#5483DF] font-medium mt-1" data-id="element-2462">
            Sistema de Control de Vendedores
          </p>
        </div>

        {/* Form Container */}
        <div className="relative z-10 w-full max-w-[420px] px-5 lg:mt-0" data-id="element-2463">
          <div className="lg:bg-transparent lg:shadow-none lg:border-0 lg:rounded-none lg:p-0 bg-white/95 backdrop-blur-md rounded-2xl p-6 sm:p-8 border border-white/20 lg:border-transparent" style={{
          boxShadow: 'var(--mobile-shadow, 0 20px 50px rgba(0,0,0,0.3))'
        }} data-id="element-2464">
            <style data-id="element-2465">{`.lg\\:shadow-none { --mobile-shadow: 0 20px 50px rgba(0,0,0,0.3); } @media (min-width: 1024px) { .lg\\:shadow-none { --mobile-shadow: none; } }`}</style>

            {/* Welcome — editorial style with time greeting */}
            <div className="mb-10 lg:text-left text-center" data-id="element-2466">
              <div className="hidden lg:inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-5 anim-fade-scale" style={{
              background: 'linear-gradient(135deg, #2952C4 0%, #5483DF 100%)',
              boxShadow: '0 4px 14px rgba(41,82,196,0.3)',
              animationDelay: '0s'
            }} data-id="element-2467">
                <TruckIcon className="w-6 h-6 text-white" data-id="element-2468" />
              </div>
              <p className="text-[13px] font-semibold text-[#2952C4] uppercase tracking-[0.2em] mb-3 anim-fade-up" style={{
              animationDelay: '0.05s'
            }} data-id="element-2469">
                Acceso al sistema
              </p>
              <h2 className="text-[32px] sm:text-[36px] font-bold text-slate-900 leading-[1.1] font-heading tracking-tight anim-fade-up" style={{
              animationDelay: '0.1s'
            }} data-id="element-2470">
                Hola de
                <br className="hidden sm:block" data-id="element-2471" /> nuevo
                <span className="text-[#2952C4]" data-id="element-2472">.</span>
              </h2>
            </div>

            {/* Form — step-style layout */}
            <form onSubmit={handleLogin} className="space-y-6" data-id="element-2473">
              {/* Step 1: Email */}
              <div className="anim-fade-up" style={{
              animationDelay: '0.2s'
            }} data-id="element-2474">
                <div className="flex items-center gap-3 mb-3" data-id="element-2475">
                  <div className="w-7 h-7 rounded-lg bg-[#eef2fc] flex items-center justify-center flex-shrink-0" data-id="element-2476">
                    <span className="text-[11px] font-bold text-[#2952C4]" data-id="element-2477">
                      01
                    </span>
                  </div>
                  <label className="text-[13px] font-semibold text-slate-700 tracking-wide" data-id="element-2478">
                    Tu correo electrónico
                  </label>
                </div>
                <div className="relative group pl-10" data-id="element-2479">
                  <div className="absolute left-[13px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#2952C4]/30 to-transparent rounded-full" data-id="element-2480" />
                  <div className="relative" data-id="element-2481">
                    <MailIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400 pointer-events-none transition-colors duration-200 group-focus-within:text-[#2952C4]" data-id="element-2482" />
                    <input type="email" value={email} onChange={e => {
                    setEmail(e.target.value);
                    setLoginError('');
                  }} placeholder="nombre@empresa.com" autoComplete="email" autoFocus required className="w-full pl-12 pr-4 py-3.5 text-[15px] rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder-slate-400 outline-none transition-all duration-200 focus:border-[#2952C4] focus:ring-4 focus:ring-[#2952C4]/10 hover:border-slate-300" data-id="element-2483" />
                  </div>
                </div>
              </div>

              {/* Step 2: Password */}
              <div className="anim-fade-up" style={{
              animationDelay: '0.3s'
            }} data-id="element-2484">
                <div className="flex items-center gap-3 mb-3" data-id="element-2485">
                  <div className="w-7 h-7 rounded-lg bg-[#eef2fc] flex items-center justify-center flex-shrink-0" data-id="element-2486">
                    <span className="text-[11px] font-bold text-[#2952C4]" data-id="element-2487">
                      02
                    </span>
                  </div>
                  <label className="text-[13px] font-semibold text-slate-700 tracking-wide" data-id="element-2488">
                    Tu contraseña
                  </label>
                </div>
                <div className="relative group pl-10" data-id="element-2489">
                  <div className="absolute left-[13px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#2952C4]/30 to-transparent rounded-full" data-id="element-2490" />
                  <div className="relative" data-id="element-2491">
                    <LockIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400 pointer-events-none transition-colors duration-200 group-focus-within:text-[#2952C4]" data-id="element-2492" />
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => {
                    setPassword(e.target.value);
                    setLoginError('');
                  }} placeholder="••••••••" autoComplete="current-password" required className="w-full pl-12 pr-12 py-3.5 text-[15px] rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder-slate-400 outline-none transition-all duration-200 focus:border-[#2952C4] focus:ring-4 focus:ring-[#2952C4]/10 hover:border-slate-300" data-id="element-2493" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} tabIndex={-1} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#2952C4] transition-colors p-1.5 rounded-lg hover:bg-slate-100" aria-label={showPassword ? 'Ocultar' : 'Mostrar'} data-id="element-2494">
                      {showPassword ? <EyeOffIcon className="w-[18px] h-[18px]" data-id="element-2495" /> : <EyeIcon className="w-[18px] h-[18px]" data-id="element-2496" />}
                    </button>
                  </div>
                </div>
              </div>

              {loginError && <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 ml-10 anim-fade-up" style={{
              animationDelay: '0s',
              animationDuration: '0.4s'
            }} data-id="element-2497">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-2498" />
                  <p className="text-[14px] text-red-700 font-medium leading-snug" data-id="element-2499">
                    {loginError}
                  </p>
                </div>}

              {/* Submit */}
              <div className="anim-fade-up pt-1" style={{
              animationDelay: '0.4s'
            }} data-id="element-2500">
                <button type="submit" disabled={isLoggingIn || !email.trim() || !password} className="relative w-full flex items-center justify-center gap-2.5 py-4 rounded-xl font-bold text-[15px] text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden group" style={{
                background: 'linear-gradient(135deg, #2952C4 0%, #1E3D96 100%)',
                boxShadow: !isLoggingIn && email.trim() && password ? '0 8px 20px rgba(41,82,196,0.25)' : 'none',
                transform: !isLoggingIn && email.trim() && password ? 'translateY(0)' : 'none'
              }} onMouseEnter={e => {
                if (!isLoggingIn && email.trim() && password) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(41,82,196,0.4)';
                }
              }} onMouseLeave={e => {
                if (!isLoggingIn && email.trim() && password) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(41,82,196,0.25)';
                }
              }} data-id="element-2501">
                  {!isLoggingIn && email.trim() && password && <div className="absolute inset-0 w-[200%] h-full pointer-events-none" style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
                  animation: 'shimmer 4s infinite'
                }} data-id="element-2502" />}
                  <span className="relative z-10 flex items-center gap-2.5" data-id="element-2503">
                    {isLoggingIn ? <>
                        <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-2504" />
                        Verificando...
                      </> : <>
                        Acceder al Sistema
                        <ArrowRightIcon className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-2505" />
                      </>}
                  </span>
                </button>
              </div>
            </form>

            {/* Minimal footer */}
            <div className="mt-8 anim-fade-up" style={{
            animationDelay: '0.5s'
          }} data-id="element-2506">
              <div className="flex items-center gap-4" data-id="element-2507">
                <div className="h-px flex-1 bg-slate-200/80" data-id="element-2508" />
                <div className="flex items-center gap-2" data-id="element-2509">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" data-id="element-2510" />
                  <span className="text-[11px] text-slate-400 font-medium" data-id="element-2511">
                    Seguro
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-2512">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2952C4]" data-id="element-2513" />
                  <span className="text-[11px] text-slate-400 font-medium" data-id="element-2514">
                    Encriptado
                  </span>
                </div>
                <div className="h-px flex-1 bg-slate-200/80" data-id="element-2515" />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden mt-6 text-center anim-fade-in relative z-10" style={{
        animationDelay: '0.8s'
      }} data-id="element-2516">
          <p className="text-[12px] text-white/30 font-medium" data-id="element-2517">
            © 2026 Miladys Rodríguez & Asoc.
          </p>
        </div>
      </div>
    </div>;
}